import React from 'react'
import MenuItem from '@material-ui/core/MenuItem'
import TextField from '@material-ui/core/TextField'

import { days, months, years } from 'src/helpers/dates'
import { Icon } from 'src/chrome'
import { useEffect, useState } from 'src/hooks'

import styles from './dateDropdown.module.scss'
import { Text } from '@sendoutcards/quantum-design-ui'

const threeUp: React.CSSProperties = {
  width: '33.333%',
  marginRight: 15,
  maxWidth: 285,
}

type Props = {
  label?: string
  initialValue?: { day: number; month: number; year?: number | null } | null
  onChange: (
    date: { day: number; month: number; year?: number | null } | null,
  ) => void
  style?: React.CSSProperties
  fields?: {
    style?: {
      marginTop: number
    }
    floatingLabelFixed: boolean
    floatingLabelStyle: React.CSSProperties
  }
}

const DateDropdown: React.FC<Props> = props => {
  const { label, initialValue, style, onChange, fields } = props

  const mutableOnChangeRef = React.useRef(onChange)
  mutableOnChangeRef.current = onChange

  const [value, setValue] = useState<{
    day: number | null
    month: number | null
    year?: number | null
  }>(initialValue || { day: null, month: null, year: null })

  const handleChange = (key: 'day' | 'month' | 'year', value: string) => {
    setValue(x => ({ ...x, [key]: parseInt(value, 10) || null }))
  }

  const handleRemove = () => {
    setValue({ day: null, month: null, year: null })
  }

  useEffect(() => {
    const { day, month, year } = value
    if (day && month) {
      mutableOnChangeRef.current({ day, month, year })
    } else if (day == null && month == null && year == null) {
      mutableOnChangeRef.current(null)
    }
  }, [value])

  return (
    <div className={styles.container}>
      {label && <Text type="caption">{label}</Text>}
      <div style={{ display: 'flex' }}>
        <TextField
          select={true}
          label="Month"
          fullWidth={true}
          value={value.month ?? ''}
          onChange={e => handleChange('month', e.target.value)}
          style={style ? style : threeUp}
          SelectProps={{
            MenuProps: {
              style: {
                maxHeight: 200,
              },
            },
          }}
          className={styles.field}
          {...fields}
        >
          {months &&
            months.map((month, index) => {
              return (
                <MenuItem
                  key={index}
                  value={month === null ? undefined : index}
                >
                  {month}
                </MenuItem>
              )
            })}
        </TextField>
        <TextField
          select={true}
          label="Day"
          fullWidth={true}
          value={value.day ?? ''}
          onChange={e => handleChange('day', e.target.value)}
          style={style ? style : threeUp}
          className={styles.field}
          SelectProps={{
            MenuProps: {
              style: {
                maxHeight: 200,
              },
            },
          }}
          {...fields}
        >
          {days &&
            days(value.month).map((day, index) => {
              return (
                <MenuItem key={index} value={day ?? undefined}>
                  {day}{' '}
                </MenuItem>
              )
            })}
        </TextField>
        <TextField
          select={true}
          label="Year"
          fullWidth={true}
          value={value.year ?? ''}
          onChange={e => handleChange('year', e.target.value)}
          style={style ? style : { width: '33.3%', maxWidth: 285 }}
          SelectProps={{
            MenuProps: {
              style: {
                maxHeight: 200,
              },
            },
          }}
          className={styles.field}
          {...fields}
        >
          {years &&
            years.map((year, index) => {
              return (
                <MenuItem key={index} value={year ?? undefined}>
                  {year}{' '}
                </MenuItem>
              )
            })}
        </TextField>
        {value.day !== null && value.month !== null && (
          <div
            style={{ display: 'flex', alignItems: 'center', paddingLeft: 8 }}
          >
            <Icon
              icon={'CLOSECIRCLE'}
              size={24}
              color={'#ff5689'}
              onClick={handleRemove}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default DateDropdown
